/**
 * Authentication Store (Zustand)
 * @description realm(Tenant/Admin)별 저장소 분리, 경로 기반 동기화
 */

import { create } from 'zustand';
import { getTokenExpiresAt } from '@shared/auth';
import { config } from '@shared/config';
import { navigateExternalWithQuery } from '@shared/router';
import type { User } from '@shared/auth';
import type { LoginCredentials } from '@/types/auth.types';
import { loginPortalAdmin, loginTenantUser, verifyTenantMfa } from '@/services/authApi';
import {
  getTenantAuthStorage,
  getAdminAuthStorage,
  getRealmFromPath,
  type AuthRealm,
} from '@/utils/authStorage';
import { setCachedPortalLogo } from '@shared/auth';
import { clearCachedPortalLogo, prefetchTenantPortalLogo } from '@/utils/tenantBranding';
import type { TenantUserLoginResponse } from '@/types/auth.types';

function tenantUserFromLoginResponse(response: TenantUserLoginResponse): User {
  const portalLogo =
    typeof response.portalLogo === 'string' && response.portalLogo.trim()
      ? response.portalLogo.trim()
      : undefined;

  return {
    userType: 'tenant-user',
    tenantId: response.tenantId,
    userId: response.userId,
    displayName: response.displayName,
    email: response.email,
    position: response.position,
    teamIds: response.teamIds,
    roles: response.roles,
    portalLogo,
    tokenExpiresAt: getTokenExpiresAt(response.token) || undefined,
  };
}

function cachePortalLogoForUser(user: User) {
  if (user.userType === 'tenant-user' && user.portalLogo?.trim()) {
    setCachedPortalLogo(user.tenantId, user.portalLogo.trim());
  }
}

function getStorage(realm: AuthRealm) {
  return realm === 'admin' ? getAdminAuthStorage() : getTenantAuthStorage();
}

/** 로그인 결과: 성공 시 ok, MFA 필요 시 2단계 정보, 최초 로그인 시 비밀번호 변경 유도 */
export type LoginResult =
  | { ok: true; mustChangePassword?: boolean }
  | { requireMfa: true; mfaChallengeId: string; userId: string; tenantId: string };

interface AuthStoreState {
  realm: AuthRealm;
  isAuthenticated: boolean;
  user: User | null;
  setRealm: (realm: AuthRealm) => void;
  loadUserFromStorage: () => void;
  login: (credentials: LoginCredentials) => Promise<LoginResult>;
  completeMfa: (mfaChallengeId: string, code: string) => Promise<{ mustChangePassword?: boolean } | void>;
  logout: () => void;
  initialize: (pathname?: string) => void;
  validateToken: () => Promise<boolean>;
  refreshToken: () => Promise<boolean>;
  navigateToService: (serviceUrl: string) => void;
}

export const useAuthStore = create<AuthStoreState>((set, get) => ({
  realm: 'tenant',
  isAuthenticated: false,
  user: null,

  setRealm: (realm: AuthRealm) => {
    set({ realm });
  },

  loadUserFromStorage: () => {
    const { realm } = get();
    const storage = getStorage(realm);
    const user = storage.getUser();
    if (user) {
      set({ isAuthenticated: true, user });
    } else {
      set({ isAuthenticated: false, user: null });
    }
  },

  login: async (credentials: LoginCredentials): Promise<LoginResult> => {
    const realm: AuthRealm = credentials.mode === 'portal-admin' ? 'admin' : 'tenant';
    const storage = getStorage(realm);
    set({ realm });

    if (credentials.mode === 'portal-admin') {
      const response = await loginPortalAdmin({
        adminUserId: credentials.adminUserId,
        password: credentials.password,
      });

      const user: User = {
        userType: 'portal-admin',
        adminUserId: response.adminUserId,
        displayName: response.adminUserId === 'admin' ? 'Portal 관리자' : response.adminUserId,
        permissions: response.permissions,
        tokenExpiresAt: getTokenExpiresAt(response.token) || undefined,
      };

      storage.setToken(response.token);
      storage.setUser(user);
      set({ isAuthenticated: true, user });
      return { ok: true };
    }

    const response = await loginTenantUser({
      tenantId: credentials.tenantId,
      userId: credentials.userId,
      password: credentials.password,
    });

    if ('requireMfa' in response && response.requireMfa) {
      return {
        requireMfa: true,
        mfaChallengeId: response.mfaChallengeId,
        userId: response.userId,
        tenantId: response.tenantId,
      };
    }

    const tenantResponse = response as TenantUserLoginResponse;
    const user = tenantUserFromLoginResponse(tenantResponse);

    storage.setToken(tenantResponse.token);
    storage.setUser(user);
    set({ isAuthenticated: true, user });
    if ((user as any).portalLogo) cachePortalLogoForUser(user);
    else void prefetchTenantPortalLogo(tenantResponse.tenantId);
    const mustChangePassword = tenantResponse.mustChangePassword === true;
    return { ok: true, mustChangePassword };
  },

  completeMfa: async (mfaChallengeId: string, code: string) => {
    const storage = getTenantAuthStorage();
    set({ realm: 'tenant' });

    const response = await verifyTenantMfa(mfaChallengeId, code);

    const user = tenantUserFromLoginResponse(response);

    storage.setToken(response.token);
    storage.setUser(user);
    set({ isAuthenticated: true, user });
    if ((user as any).portalLogo) cachePortalLogoForUser(user);
    else void prefetchTenantPortalLogo(response.tenantId);

    const mustChangePassword = response.mustChangePassword === true;
    return mustChangePassword ? { mustChangePassword: true } : undefined;
  },

  logout: () => {
    const { realm, user } = get();
    if (user?.userType === 'tenant-user' && user.tenantId) {
      clearCachedPortalLogo(user.tenantId);
    }
    getStorage(realm).clear();
    set({ isAuthenticated: false, user: null });
  },

  validateToken: async () => {
    const { realm } = get();
    const storage = getStorage(realm);
    if (!storage.hasAuth()) {
      set({ isAuthenticated: false, user: null });
      return false;
    }

    try {
      const userData = storage.getUser();
      const token = storage.getToken();

      if (!userData || !token) return false;

      const endpoint = userData.userType === 'portal-admin' ? '/admin/auth/me' : '/auth/me';
      const headers: HeadersInit = { Authorization: `Bearer ${token}` };
      if (userData.userType === 'tenant-user') {
        headers['X-Tenant-Id'] = userData.tenantId;
      }

      const response = await fetch(`${config.apiUrl}/api/portal${endpoint}`, { headers });

      if (response.status === 401) {
        storage.clear();
        set({ isAuthenticated: false, user: null });
        return false;
      }
      if (!response.ok) {
        set({ isAuthenticated: false, user: null });
        return false;
      }

      return true;
    } catch (error) {
      // 네트워크 오류 등: 저장소 비우지 않음
      set({ isAuthenticated: false, user: null });
      return false;
    }
  },

  initialize: (pathname?: string) => {
    if (!pathname) return;
    const realm = getRealmFromPath(pathname);
    set({ realm });
    const storage = getStorage(realm);
    const user = storage.getUser();
    if (user) {
      set({ isAuthenticated: true, user });
      if (user.userType === 'tenant-user' && user.tenantId) {
        void prefetchTenantPortalLogo(user.tenantId);
      }
    } else {
      set({ isAuthenticated: false, user: null });
    }
  },

  navigateToService: (serviceUrl: string) => {
    const { realm } = get();
    const storage = getStorage(realm);
    const token = storage.getToken();
    const user = storage.getUser();

    if (!token || !user) {
      console.error('No auth data to navigate');
      return;
    }

    navigateExternalWithQuery(serviceUrl, {
      token,
      user: encodeURIComponent(JSON.stringify(user)),
    });
  },

  refreshToken: async () => {
    const { realm } = get();
    const storage = getStorage(realm);
    const token = storage.getToken();
    const user = storage.getUser();

    if (!token || !user) return false;

    try {
      const endpoint = user.userType === 'portal-admin' ? '/admin/auth/refresh' : '/auth/refresh';
      const headers: HeadersInit = { Authorization: `Bearer ${token}` };
      if (user.userType === 'tenant-user') {
        headers['X-Tenant-Id'] = user.tenantId;
      }

      const response = await fetch(`${config.apiUrl}/api/portal${endpoint}`, {
        method: 'POST',
        headers,
      });

      if (!response.ok) return false;

      const data = await response.json();
      // 연장 후 만료시각은 JWT exp(백엔드의 tenant 보안정책 timeoutSeconds 반영) 기준으로 저장
      const tokenExpiresAt =
        data.tokenExpiresAt ||
        getTokenExpiresAt(data.token) ||
        (user.tokenExpiresAt ?? Date.now() + 60 * 60 * 1000);
      const updatedUser = { ...user, tokenExpiresAt };

      storage.setToken(data.token);
      storage.setUser(updatedUser);
      set({ user: updatedUser });

      return true;
    } catch (error) {
      const isNetworkError = error instanceof TypeError && ((error as Error).message === 'Failed to fetch' || (error as Error).message?.includes('fetch'));
      if (isNetworkError) {
        console.warn('Token refresh skipped: backend server unreachable (connection refused).');
      } else {
        console.error('Token refresh failed:', error);
      }
      return false;
    }
  },
}));
